"use client";

import {
  ButtonHTMLAttributes,
  cloneElement,
  FC,
  ReactElement,
  useCallback,
  useRef,
  useState,
} from "react";
import Button from "./Button";
import Dialog from "./Dialog";
import { Field, Input } from "@chakra-ui/react";

type Props = {
  header: string;
  trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
  requiredPhase?: string;
  children: ReactElement;
  onConfirmRemove: VoidFunction;
};

const RemoveModal: FC<Props> = ({
  header,
  trigger,
  requiredPhase = "",
  children,
  onConfirmRemove,
}) => {
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const onConfirm = () => {
    setIsOpen(false);
    onConfirmRemove();
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const triggerEl = cloneElement(trigger, {
    onClick: () => setIsOpen(true),
  });

  const isEnabled = text.toLowerCase() === requiredPhase.toLowerCase();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      onInteractOutside={onClose}
      onEscapeKeyDown={onClose}
      initialFocusEl={() => cancelRef.current ?? null}
      trigger={triggerEl}
      header={header}
      actions={[
        <Button key={0} onClick={() => setIsOpen(false)} ref={cancelRef}>
          Cancel
        </Button>,
        <Button
          key={1}
          visual="danger"
          disabled={!isEnabled}
          onClick={onConfirm}
        >
          Confirm
        </Button>,
      ]}
    >
      {children}
      {requiredPhase && (
        <Field.Root mt={5}>
          <Field.Label>
            Type "{requiredPhase}" into the field below:
          </Field.Label>
          <Input value={text} onChange={event => setText(event.target.value)} />
        </Field.Root>
      )}
    </Dialog>
  );
};

export default RemoveModal;
